/**
 * IncidentCreatePage — file a new incident. Mobile-friendly single-
 * column form. Type-driven conditional fields: only "injury" shows
 * OSHA/treatment fields; only when a project is selected do we
 * auto-fill site/customer.
 *
 * The `?project=<id>` query param pre-selects a project, so opening
 * this page from a project's detail action lands you ready to file.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ShieldAlert, AlertTriangle, HeartPulse, Wrench } from "lucide-react";

const TYPE_CHOICES = [
  { value: "stop_work",       label: "Stop-Work",       icon: ShieldAlert,   desc: "Work halted for a safety concern" },
  { value: "injury",          label: "Injury",          icon: HeartPulse,    desc: "Person hurt on the job" },
  { value: "near_miss",       label: "Near Miss/Good Catch", icon: AlertTriangle, desc: "Could have been worse — or a hazard someone caught before harm. Capture the lesson." },
  { value: "property_damage", label: "Property Damage", icon: Wrench,        desc: "Equipment / vehicle / site damage" },
];

const SEV_CHOICES = [
  { value: "low",      label: "Low" },
  { value: "medium",   label: "Medium" },
  { value: "high",     label: "High" },
  { value: "critical", label: "Critical" },
];

const TREATMENT_CHOICES = [
  { value: "",           label: "— select —" },
  { value: "first_aid",  label: "First aid on site" },
  { value: "clinic",     label: "Clinic (no lost time)" },
  { value: "hospital",   label: "Hospital / emergency room" },
  { value: "fatality",   label: "Fatality" },
];

const AGENCY_CHOICES = ["EMS / Ambulance", "Fire Department", "Police", "OSHA", "State DOT", "Utility company"];

function nowLocalISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
}

export default function IncidentCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preProjectId = new URLSearchParams(location.search).get("project") || "";

  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    type: "near_miss",
    severity: "medium",
    project_id: preProjectId,
    site_number: "",
    customer: "",
    occurred_at: nowLocalISO(),
    occurred_location: "",
    description: "",
    immediate_action_taken: "",
    injured_person_name: "",
    body_part: "",
    treatment_level: "",
    days_away: 0,
    days_restricted: 0,
    osha_recordable: false,
    osha_reportable: false,
    emergency_services_contacted: false,
    emergency_services_dispatched: false,
    emergency_agencies: "",
    emergency_arrival_time: "",
    emergency_case_number: "",
    emergency_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data.items || r.data || [])).catch(() => setProjects([]));
  }, []);

  // Autofill site + customer from picked project so the reporter
  // doesn't retype what the system already knows.
  useEffect(() => {
    if (!form.project_id) return;
    const p = projects.find((x) => x.id === form.project_id);
    if (p) setForm((f) => ({ ...f, site_number: f.site_number || p.site_number || "", customer: f.customer || p.customer || "" }));
  }, [form.project_id, projects]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.description.trim()) return toast.error("Describe what happened.");
    if (!form.occurred_at) return toast.error("When did it happen?");
    if (form.type === "injury" && !form.injured_person_name.trim())
      return toast.error("Who was injured?");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        project_id: form.project_id || null,
        occurred_at: new Date(form.occurred_at).toISOString(),
        days_away: Number(form.days_away) || 0,
        days_restricted: Number(form.days_restricted) || 0,
      };
      const r = await api.post("/incidents", payload);
      toast.success("Incident filed.");
      navigate(`/incidents/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to file incident.");
    } finally {
      setSubmitting(false);
    }
  };

  const isInjury = form.type === "injury";
  const isHighSeverity = form.severity === "high" || form.severity === "critical";
  const toggleAgency = (a) => {
    const list = (form.emergency_agencies || "").split(",").map((s) => s.trim()).filter(Boolean);
    const idx = list.indexOf(a);
    if (idx >= 0) list.splice(idx, 1);
    else list.push(a);
    setField("emergency_agencies", list.join(", "));
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto space-y-4">
        <Link to="/incidents" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200" data-testid="incident-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Incidents
        </Link>
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-400 font-display">Safety</div>
          <h1 className="font-display font-black text-3xl tracking-tight" data-testid="incident-create-heading">Report Incident</h1>
          <p className="text-sm text-slate-400 mt-1">Capture the essentials now — you can add corrective actions and evidence photos after saving.</p>
        </div>

        {/* Type picker — big-tap cards, mobile-first */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5 space-y-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">Type</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TYPE_CHOICES.map((t) => {
                const Icon = t.icon;
                const active = form.type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setField("type", t.value)}
                    className={`text-left p-3 rounded-md border transition-colors ${
                      active
                        ? "border-red-600 bg-red-950/40 text-white"
                        : "border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800"
                    }`}
                    data-testid={`incident-type-${t.value}`}
                  >
                    <Icon className={`w-5 h-5 mb-1.5 ${active ? "text-red-400" : "text-slate-400"}`} />
                    <div className="font-display font-bold text-sm">{t.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{t.desc}</div>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <Field label="Severity">
                <select value={form.severity} onChange={(e) => setField("severity", e.target.value)}
                  className="w-full h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm"
                  data-testid="incident-severity">
                  {SEV_CHOICES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="When it happened">
                <Input type="datetime-local" value={form.occurred_at} onChange={(e) => setField("occurred_at", e.target.value)}
                  className="bg-slate-950 border-slate-700" data-testid="incident-occurred-at" />
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5 space-y-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">Where</Label>
            <Field label="Link to project (optional)">
              <select value={form.project_id} onChange={(e) => setField("project_id", e.target.value)}
                className="w-full h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm"
                data-testid="incident-project-select">
                <option value="">— No project (standalone) —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.site_number} · {p.site_name || p.project_name} · {p.customer}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Site #">
                <Input value={form.site_number} onChange={(e) => setField("site_number", e.target.value)} className="bg-slate-950 border-slate-700" data-testid="incident-site-number" />
              </Field>
              <Field label="Customer">
                <Input value={form.customer} onChange={(e) => setField("customer", e.target.value)} className="bg-slate-950 border-slate-700" data-testid="incident-customer" />
              </Field>
            </div>
            <Field label='Location on site (e.g. "north tower base")'>
              <Input value={form.occurred_location} onChange={(e) => setField("occurred_location", e.target.value)} className="bg-slate-950 border-slate-700" data-testid="incident-location" />
            </Field>
          </CardContent>
        </Card>

        {/* What happened */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5 space-y-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">What happened</Label>
            <Field label="Description *">
              <Textarea rows={4} value={form.description} onChange={(e) => setField("description", e.target.value)}
                placeholder="What did you observe? What was the sequence of events?"
                className="bg-slate-950 border-slate-700" data-testid="incident-description" />
            </Field>
            <Field label="Immediate action taken">
              <Textarea rows={2} value={form.immediate_action_taken} onChange={(e) => setField("immediate_action_taken", e.target.value)}
                placeholder="What did you do right after?"
                className="bg-slate-950 border-slate-700" data-testid="incident-immediate-action" />
            </Field>
          </CardContent>
        </Card>

        {/* Emergency Services — surfaced whenever severity is high
            or critical. Optional but visible so a PM never has to
            hunt for "did we call 911?" on a serious event. Storage
            is unconditional so escalations don't lose data. */}
        {isHighSeverity && (
          <Card className="bg-red-950/30 border-red-900" data-testid="incident-emergency-section">
            <CardContent className="p-5 space-y-3">
              <Label className="text-[10px] uppercase tracking-wider text-red-300 font-display font-bold">
                Emergency Services {form.severity === "critical" ? "(critical severity)" : "(high severity)"}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={form.emergency_services_contacted}
                    onChange={(e) => setField("emergency_services_contacted", e.target.checked)}
                    data-testid="incident-emergency-contacted"
                  />
                  Emergency services were <b>contacted</b>
                </label>
                <label className={`inline-flex items-center gap-2 cursor-pointer text-sm ${!form.emergency_services_contacted ? "opacity-50" : ""}`}>
                  <input
                    type="checkbox"
                    checked={form.emergency_services_dispatched}
                    onChange={(e) => setField("emergency_services_dispatched", e.target.checked)}
                    disabled={!form.emergency_services_contacted}
                    data-testid="incident-emergency-dispatched"
                  />
                  Emergency services were <b>dispatched</b> to scene
                </label>
              </div>

              {form.emergency_services_contacted && (
                <>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold block mb-1">
                      Agencies contacted
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {AGENCY_CHOICES.map((a) => {
                        const active = (form.emergency_agencies || "").split(",").map((s) => s.trim()).includes(a);
                        return (
                          <button
                            key={a}
                            type="button"
                            onClick={() => toggleAgency(a)}
                            className={`h-7 px-2.5 rounded-full text-[11px] font-display font-bold uppercase tracking-wider transition-colors border ${
                              active
                                ? "bg-red-700 border-red-500 text-white"
                                : "border-slate-700 bg-slate-950 text-slate-300 hover:bg-slate-800"
                            }`}
                            data-testid={`incident-emergency-agency-${a.split(" ")[0].toLowerCase()}`}
                          >
                            {a}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      value={form.emergency_agencies}
                      onChange={(e) => setField("emergency_agencies", e.target.value)}
                      placeholder='Or type here — comma-separated (e.g. "EMS, Fire, Police")'
                      className="mt-2 bg-slate-950 border-slate-700"
                      data-testid="incident-emergency-agencies-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Field label="Arrival time">
                      <Input
                        type="datetime-local"
                        value={form.emergency_arrival_time}
                        onChange={(e) => setField("emergency_arrival_time", e.target.value)}
                        className="bg-slate-950 border-slate-700"
                        data-testid="incident-emergency-arrival"
                      />
                    </Field>
                    <Field label="Case / report number">
                      <Input
                        value={form.emergency_case_number}
                        onChange={(e) => setField("emergency_case_number", e.target.value)}
                        placeholder="e.g. 26-004291"
                        className="bg-slate-950 border-slate-700"
                        data-testid="incident-emergency-case"
                      />
                    </Field>
                  </div>
                  <Field label="Additional notes (responder names, scene interactions)">
                    <Textarea
                      rows={2}
                      value={form.emergency_notes}
                      onChange={(e) => setField("emergency_notes", e.target.value)}
                      className="bg-slate-950 border-slate-700"
                      data-testid="incident-emergency-notes"
                    />
                  </Field>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Injury-only section */}
        {isInjury && (
          <Card className="bg-red-950/20 border-red-900" data-testid="incident-injury-section">
            <CardContent className="p-5 space-y-3">
              <Label className="text-[10px] uppercase tracking-wider text-red-300 font-display font-bold">Injury details (OSHA Form 301)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Injured person's name *">
                  <Input value={form.injured_person_name} onChange={(e) => setField("injured_person_name", e.target.value)} className="bg-slate-950 border-slate-700" data-testid="incident-injured-name" />
                </Field>
                <Field label="Body part(s) affected">
                  <Input value={form.body_part} onChange={(e) => setField("body_part", e.target.value)} className="bg-slate-950 border-slate-700" data-testid="incident-body-part" />
                </Field>
                <Field label="Treatment level">
                  <select value={form.treatment_level} onChange={(e) => setField("treatment_level", e.target.value)}
                    className="w-full h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm"
                    data-testid="incident-treatment">
                    {TREATMENT_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <div />
                <Field label="Days away from work">
                  <Input type="number" min="0" value={form.days_away} onChange={(e) => setField("days_away", e.target.value)} className="bg-slate-950 border-slate-700" data-testid="incident-days-away" />
                </Field>
                <Field label="Days on restricted duty">
                  <Input type="number" min="0" value={form.days_restricted} onChange={(e) => setField("days_restricted", e.target.value)} className="bg-slate-950 border-slate-700" data-testid="incident-days-restricted" />
                </Field>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.osha_recordable} onChange={(e) => setField("osha_recordable", e.target.checked)} data-testid="incident-osha-recordable" />
                  OSHA recordable
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.osha_reportable} onChange={(e) => setField("osha_reportable", e.target.checked)} data-testid="incident-osha-reportable" />
                  OSHA reportable (fatality / hospitalization / amputation)
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => navigate("/incidents")} className="border-slate-700 bg-slate-900 hover:bg-slate-800" data-testid="incident-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="incident-submit-btn">
            {submitting ? "FILING…" : "FILE INCIDENT"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold block mb-1">{label}</Label>
      {children}
    </div>
  );
}
